<template>
    <div :class="size == 'naked' ? '' : 'afu-relative'">
        <C1FormInput
            ref="input"
            v-model="keyword"
            :label="label"
            :label-for="labelFor"
            :placeholder="placeholder"
            :size="size"
            :is-invalid="isInvalid"
            :invalid-feedback="invalidFeedback"
            :disabled="disabled"
            :show-x="!disabled"
            @input="debouncedInput"
            @clear="clearAll"
            @focus="isFocused = true"
            @blur="onBlur"
            @keydown="onKeydown"
            v-on="inputListeners"
        >
            <template
                v-if="!noIcon"
                v-slot:icon
            >
                <slot name="icon">
                    <FontAwesomeIcon
                        :icon="['fal', 'search']"
                        class="afu-text-neutral-dark"
                    />
                </slot>
            </template>
        </C1FormInput>

        <div
            v-if="isBusy"
            class="afu-absolute afu-right-0 afu-mt-2 afu-w-full afu-rounded-card afu-shadow-lg afu-z-50"
            style="top: calc(100%);"
        >
            <div class="afu-text-sm afu-p-3 afu-rounded-card afu-bg-white afu-shadow-xs afu-text-gray-600 afu-text-medium">
                <C1Spinner class="mr-2" />
            </div>
        </div>

        <div
            v-if="isFocused && !options.length && keyword.length >= searchMinLength && $slots['noResults'] && !isBusy"
            class="afu-absolute afu-right-0 afu-mt-2 afu-w-full afu-rounded-card afu-shadow-lg afu-z-50"
            style="top: calc(100%);"
        >
            <div class="afu-text-sm afu-p-3 afu-rounded-card afu-bg-white afu-shadow-xs afu-text-neutral-dark afu-text-normal">
                <slot name="noResults" />
            </div>
        </div>

        <Transition
            enter-active-class="afu-transition afu-ease-out afu-duration-100"
            enter-class="afu-transform afu-opacity-0 afu-scale-95"
            enter-to-class="afu-transform afu-opacity-100 afu-scale-100"
            leave-active-class="afu-transition afu-ease-in afu-duration-75"
            leave-class="afu-transform afu-opacity-100 afu-scale-100"
            leave-to-class="afu-transform afu-opacity-0 afu-scale-95"
        >
            <div
                v-show="isFocused && (mutableOptions.length || showFirstElement) && keyword.length >= searchMinLength && !isBusy"
                class="afu-absolute afu-right-0 afu-mt-2 afu-w-full afu-rounded-card afu-shadow-lg afu-z-50 afu-overflow-y-scroll"
                style="max-height: 200px; top: calc(100%);"
            >
                <C1TypeaheadList
                    ref="typeaheadList"
                    :options="mutableOptions"
                    :label-key="labelKey"
                    :value-key="valueKey"
                    :arrow-counter.sync="arrowCounter"
                    :show-first-element="showFirstElement"
                    @firstElementClick="firstElementClick(keyword)"
                    :keyword="keyword"
                    @select="onSelect"
                />
            </div>
        </Transition>
    </div>
</template>

<script>
    import C1FormInput from './C1FormInput';
    import C1TypeaheadList from '@/components/C1Components/C1TypeaheadList';
    import C1Spinner from './C1Spinner';
    import debounce from 'lodash/debounce';

    export default {
        name: 'C1Typeahead',

        components: {
            C1TypeaheadList,
            C1FormInput,
            C1Spinner,
        },

        props: {
            value: {
                type: String,
                default: '',
            },

            label: {
                type: String,
                default: '',
            },

            labelFor: {
                type: String,
                default: '',
            },

            placeholder: {
                type: String,
                default: '',
            },

            options: {
                type: Array,
                default: () => [],
            },

            labelKey: {
                type: String,
                default: 'label',
            },

            valueKey: {
                type: String,
                default: 'id',
            },

            filterBy: {
                type: String,
                default: null,
            },

            searchMinLength: {
                type: Number,
                default: 3,
            },

            cleanAfterSelect: {
                type: Boolean,
                default: false,
            },

            isBusy: {
                type: Boolean,
                default: false,
            },

            size: {
                type: String,
                default: null,
                validator: value => ['sm', 'lg', 'naked'].includes(value),
            },

            isInvalid: {
                type: Boolean,
                default: false,
            },

            invalidFeedback: {
                type: [String, Boolean],
                default: false,
            },

            noIcon: {
                type: Boolean,
                default: false,
            },

            allowNew: {
                type: Boolean,
                default: false,
            },

            disabled: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                isFocused: false,
                keyword: '',
                id: this._uid,
                arrowCounter: this.allowNew ? -1 : 0,
                originalOptions: [],
                mutableOptions: [],
                defaultFilter: this.filterBy || this.labelKey,
            };
        },

        computed: {
            inputListeners() {
                const vm = this;
                return {
                    ...this.$listeners,
                    input(value) {
                        vm.$emit('input', value);
                    },
                    blur(event) {
                        vm.$emit('blur', event);
                    },
                };
            },

            showFirstElement() {
                return (
                    this.allowNew &&
                    this.keyword.length >= this.searchMinLength &&
                    !this.ifValueExists(this.keyword)
                );
            },
        },

        watch: {
            value(value) {
                this.keyword = value;
            },

            options() {
                this.cloneOptions();
            },
        },

        created() {
            this.keyword = this.value;

            this.cloneOptions();
        },

        methods: {
            // eslint-disable-next-line
            debouncedInput: debounce(function() {
                const keyword = this.keyword;
                this.emitInput();

                if (keyword.length >= this.searchMinLength) {
                    this.$emit('search', keyword);
                } else {
                    this.resetOptions();
                }
            }, 400),

            onSelect() {
                const selected = this.mutableOptions[this.arrowCounter];
                const selectedOption = this.options.find(
                    o => o[this.valueKey] === selected[this.valueKey],
                );
                if (selectedOption) {
                    this.$emit('hit', selectedOption);
                    this.keyword = selectedOption[this.labelKey];
                    this.emitInput();
                    this.resetArrowCounter();

                    if (this.cleanAfterSelect) {
                        this.resetKeyword();
                    }

                    this.resetOptions();
                }
            },

            searchInternally() {
                this.mutableOptions = this.originalOptions;
                this.highlightOptions();
            },

            highlightOptions() {
                const search = this.keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const query = new RegExp(search, 'i');
                this.mutableOptions.forEach(o => {
                    this.$set(
                        o,
                        `${this.labelKey}_highlighted`,
                        o[this.labelKey].replace(
                            query,
                            '<span class="afu-font-semibold">$&</span>',
                        ),
                    );
                });
            },

            cloneOptions() {
                this.originalOptions = JSON.parse(JSON.stringify(this.options));
                this.mutableOptions = JSON.parse(JSON.stringify(this.options));
                this.searchInternally();
            },

            onBlur(evt) {
                const tgt = evt.relatedTarget;
                if (tgt && tgt.classList.contains('typeahead-item')) {
                    return;
                }

                this.$emit('blur', evt);

                this.isFocused = false;
            },

            onKeydown(evt) {
                if (!this.mutableOptions.length) {
                    return;
                }

                switch (evt.code) {
                    case 'ArrowUp':
                        evt.preventDefault();
                        this.onArrowUp();
                        break;
                    case 'ArrowDown':
                        evt.preventDefault();
                        this.onArrowDown();
                        break;
                    case 'Enter':
                        this.onSelect();
                        break;
                    case 'Escape':
                        this.onEsc();
                        break;
                }
            },

            onEsc() {
                this.$refs.input.$refs.input.blur();
                this.isFocused = false;
                this.resetArrowCounter();
            },

            onArrowDown() {
                if (this.arrowCounter < this.mutableOptions.length - 1) {
                    this.arrowCounter += 1;
                }
                this.fixScrolling();
            },

            onArrowUp() {
                if (this.showFirstElement && this.arrowCounter > -1) {
                    this.arrowCounter -= 1;
                } else if (!this.showFirstElement && this.arrowCounter > 0) {
                    this.arrowCounter -= 1;
                }

                this.fixScrolling();
            },

            fixScrolling() {
                const ref = `option_${this.arrowCounter}`;
                const element =
                    this.arrowCounter >= 0
                        ? this.$refs.typeaheadList.$refs[ref][0]
                        : this.$refs.typeaheadList.$refs[ref];
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'start',
                });
            },

            setArrowCounter(number) {
                this.arrowCounter = number;
            },

            resetArrowCounter() {
                if (this.allowNew) {
                    this.arrowCounter = -1;
                } else {
                    this.arrowCounter = 0;
                }
            },

            emitInput() {
                this.$emit('input', this.keyword);
            },

            resetOptions() {
                this.originalOptions = [];
                this.mutableOptions = [];
            },

            resetKeyword() {
                this.keyword = '';
                this.emitInput();
            },

            clearAll() {
                this.$emit('hit', null);
                this.resetKeyword();
                this.resetOptions();
            },

            ifValueExists(option) {
                return !!this.options.find(
                    o =>
                        o[this.defaultFilter].toLowerCase() === option.toLowerCase(),
                );
            },

            firstElementClick(keyword) {
                this.$emit('hit', keyword);

                if (this.cleanAfterSelect) {
                    this.resetKeyword();
                }

                this.resetArrowCounter();
                this.resetOptions();
                this.isFocused = false;
            },
        },
    };
</script>
